import { DriverDto, TruckDto } from '@trucktrax/trucktrax-ts-common';
import { createSelector } from 'reselect';
import { Driver } from '@trucktrax/trucktrax-common';

import { ReduxState } from '..';
import { getFullName } from '../../util/adminUtil';
import { DataItem } from '../../components/shared/messages/AutoCompleteWithChip';
import { DriverWithProductLine } from '../../components/shared/messages/MessageCenterContainer';
import { orderAscByProperty } from '../../util/appUtil';

const getAllTrucks = (state: ReduxState): TruckDto[] => state.allTrucks || [];
const getDriverList = (state: ReduxState): DriverDto[] => state.driverList?.drivers || [];
const getAllDriverList = (state: ReduxState): DriverDto[] => state.allDrivers || [];

export const getTruckDriverMapping = createSelector(
  [getAllTrucks, getDriverList],
  (trucks: TruckDto[]): Map<string, string> => buildTruckMap(trucks),
);

export function buildTruckMap(trucks: TruckDto[]): Map<string, string> {
  const mapping = new Map<string, string>();
  const validTrucksWithDrivers = trucks.filter(truck => truck.truckAlias && truck?.driver?.url);

  validTrucksWithDrivers.forEach(truck => {
    const driverUrl = truck?.driver?.url;
    mapping.set(driverUrl!, truck.truckAlias!);
  });

  return mapping;
}

export const getTruckAliasLookup = createSelector(
  [getTruckDriverMapping],
  (mapping: Map<string, string>) =>
    (driverUrl?: string): string | undefined =>
      driverUrl ? mapping.get(driverUrl) : undefined,
);

export const getTruckDriverData = createSelector(
  [getTruckDriverMapping, getTruckAliasLookup],
  (mapping, lookupFn) => ({
    mapping,
    getTruckAlias: lookupFn,
  }),
);

export const getDriversWithDetails = createSelector(
  [getDriverList, getTruckDriverMapping],
  (drivers, truckDriverMap) => {
    const modifiedDrivers = includeExtraDriverDetails(drivers, truckDriverMap);
    return orderAscByProperty(modifiedDrivers, 'fullName');
  },
);

export const getAllDriversWithDetails = createSelector(
  [getAllDriverList, getTruckDriverMapping],
  (drivers, truckDriverMap) => {
    const modifiedDrivers = includeExtraDriverDetails(drivers, truckDriverMap);
    return orderAscByProperty(modifiedDrivers, 'fullName');
  },
);

export function includeExtraDriverDetails(
  drivers: DriverDto[],
  truckDriverMap: Map<string, string>,
): DriverDetail[] {
  const modifiedDrivers = drivers.map((driver: DriverDto) => {
    const fullName = getFullName(driver);
    const { url } = driver;
    const truckAlias = truckDriverMap.get(url ?? '');
    const label = truckAlias ? `${fullName} (${truckAlias})` : fullName;
    const modifiedDriver = {
      ...driver,
      fullName,
      label,
    } as DriverDetail;
    return modifiedDriver;
  });

  return orderAscByProperty(modifiedDrivers, 'fullName');
}

export type DriverDetail = Driver & DriverDto & DataItem & DriverWithProductLine;
